import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import {
  LinkBarPropsNoStyle,
  LinkBarDefinition,
  LinkBarCarmiProps,
  LinkBarCSSVars,
} from '../LinkBar.types';

export const props = withCompInfo<
  LinkBarPropsNoStyle,
  LinkBarDefinition,
  LinkBarCarmiProps
>()(['compProps', 'experiments'], ({ compProps, experiments }, carmiData) => {
  const { iconSize } = compProps;
  return {
    ...carmiData,
    iconSize,
    shouldRenderPlaceholders:
      experiments['specs.thunderbolt.LinkBarPlaceholderImages'] === true,
  };
});

const calculateMobileSize = (
  numOfItems: number,
  iconSize: number,
  spacing: number,
  isHorizontal: boolean,
  totalItemSize: number,
) => {
  if (numOfItems === 0) {
    return { height: `${iconSize}px`, width: '5px' };
  }

  const itemSize = iconSize + spacing;
  const actualTotalSize = totalItemSize > 300 ? 300 : totalItemSize;
  const width = isHorizontal ? actualTotalSize : iconSize;
  const height = isHorizontal
    ? Math.ceil(numOfItems / Math.floor((width + spacing) / itemSize)) *
      iconSize
    : totalItemSize;
  return { width: `${width}px`, height: `${height}px` };
};

const calculateDesktopSize = (
  numOfItems: number,
  iconSize: number,
  isHorizontal: boolean,
  totalItemSize: number,
) => {
  if (numOfItems === 0) {
    return { height: `${iconSize}px`, width: `${iconSize}px` };
  }

  return {
    width: `${isHorizontal ? totalItemSize : iconSize}px`,
    height: `${isHorizontal ? iconSize : totalItemSize}px`,
  };
};

const calculateResponsiveSize = (
  numOfItems: number,
  isHorizontal: boolean,
  totalItemSize: number,
) => {
  return numOfItems > 0 && !isHorizontal
    ? { height: `${totalItemSize}px` }
    : {};
};

const calculteSize = ({
  hasResponsiveLayout,
  isMobileView,
  compProps,
  compData,
}: {
  hasResponsiveLayout: boolean;
  isMobileView: boolean;
  compProps: LinkBarDefinition['property'];
  compData: LinkBarDefinition['data'];
}): { width?: string; height?: string } => {
  const numOfItems = compData.items?.length ?? 0;
  const totalItemSize =
    numOfItems * (compProps.iconSize + compProps.spacing) - compProps.spacing;
  const isHorizontal = compProps.orientation === 'HORIZ';
  if (hasResponsiveLayout) {
    return calculateResponsiveSize(numOfItems, isHorizontal, totalItemSize);
  }
  if (isMobileView) {
    return calculateMobileSize(
      numOfItems,
      compProps.iconSize,
      compProps.spacing,
      isHorizontal,
      totalItemSize,
    );
  }
  return calculateDesktopSize(
    numOfItems,
    compProps.iconSize,
    isHorizontal,
    totalItemSize,
  );
};

export const css = withCompInfo<LinkBarCSSVars, LinkBarDefinition>()(
  ['hasResponsiveLayout', 'compProps', 'compData', 'isMobileView'],
  ({ hasResponsiveLayout, compProps, compData, isMobileView }) => {
    const isHorizontal = compProps.orientation === 'HORIZ';

    return {
      '--item-size': `${compProps.iconSize}px`,
      '--item-margin': isHorizontal
        ? `0 ${compProps.spacing}px 0 0`
        : `0 0 ${compProps.spacing}px 0`,
      '--item-display': isHorizontal ? 'inline-block' : 'block',
      ...(hasResponsiveLayout && {
        '--flex-direction': isHorizontal ? 'row' : 'column',
      }),
      ...calculteSize({
        hasResponsiveLayout,
        isMobileView,
        compProps,
        compData,
      }),
    };
  },
);

export default createComponentMapperModel({ props, css });
